import { useEffect, useRef } from 'react';

/**
 * Locks the body against scroll while `active` is true. Plain `overflow:
 * hidden` on <body> is not reliable on iOS Safari — it still allows
 * rubber-band touch-scrolling of the content behind a fixed overlay. Pinning
 * the body to `position: fixed` at the current scroll offset (and restoring
 * both the styles and the exact scroll position on close) is the standard
 * robust fix. Cleanup also runs if the component unmounts while still active.
 */
export default function useBodyScrollLock(active) {
  const scrollYRef = useRef(0);

  useEffect(() => {
    if (!active) return undefined;

    const { body } = document;
    scrollYRef.current = window.scrollY;

    const prevStyle = {
      position: body.style.position,
      top: body.style.top,
      left: body.style.left,
      width: body.style.width,
      overflow: body.style.overflow,
    };

    body.style.position = 'fixed';
    body.style.top = `-${scrollYRef.current}px`;
    body.style.left = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';

    return () => {
      body.style.position = prevStyle.position;
      body.style.top = prevStyle.top;
      body.style.left = prevStyle.left;
      body.style.width = prevStyle.width;
      body.style.overflow = prevStyle.overflow;
      window.scrollTo(0, scrollYRef.current);
    };
  }, [active]);
}
